import { sb } from '../supabase';

// Growth OS — ad spend (read-only pulls) joined to OUR attribution.
// Spend, impressions, clicks, installs and the platform's own conversions come
// from growth_ad_daily. Sign-ups and paid orders per campaign come from
// growth_attribution: a touch counts for a campaign when its utm_source is the
// platform's name and its utm_campaign is the campaign's ID — the UTM rule
// given to the agency (utm_campaign = {campaignid} for Google). A gclid alone
// cannot be tied to a campaign without Google's click report, so it is counted
// as «من Google، حملة غير محددة».
// Meta is pulled at AD level too (growth_ad_ads / growth_ad_ad_daily). The
// agency's Meta link, everywhere (owner, 2026-11-02):
//   utm_source=meta&utm_medium=paid_social&utm_campaign={{campaign.id}}&utm_content={{ad.id}}
// utm_content (the ad ID) credits the ad; utm_medium is recorded with the touch
// but not required for credit, so a link missing it still counts.

export const PLATFORMS = [
  { key: 'google', ar: 'Google Ads', utm: ['google'] },
  { key: 'meta', ar: 'Meta', utm: ['meta', 'facebook', 'instagram'] },
  { key: 'snapchat', ar: 'Snapchat', utm: ['snapchat'], oauth: true },
  { key: 'tiktok', ar: 'TikTok', utm: ['tiktok'], later: 'تطبيق المطوّر قيد المراجعة' },
];
export const STATUS_AR = { ENABLED: 'تعمل', ACTIVE: 'تعمل', PAUSED: 'متوقفة', REMOVED: 'محذوفة', UNKNOWN: '—',
  // Meta's effective_status
  CAMPAIGN_PAUSED: 'الحملة متوقفة', ADSET_PAUSED: 'المجموعة متوقفة', ARCHIVED: 'مؤرشفة', DELETED: 'محذوفة',
  IN_PROCESS: 'قيد المعالجة', PENDING_REVIEW: 'قيد المراجعة', DISAPPROVED: 'مرفوضة', WITH_ISSUES: 'بها مشكلة' };

const lower = (v) => String(v || '').trim().toLowerCase();

// Which platform + campaign a touch credits, or null.
export function creditOf(touch = {}) {
  const src = lower(touch.utm_source);
  const p = PLATFORMS.find((x) => x.utm.includes(src));
  if (p) return { platform: p.key, campaign_id: String(touch.utm_campaign || '').trim() || null, ad_id: String(touch.utm_content || '').trim() || null };
  if (touch.gclid) return { platform: 'google', campaign_id: null };
  if (touch.fbclid) return { platform: 'meta', campaign_id: null };
  if (touch.ttclid) return { platform: 'tiktok', campaign_id: null };
  return null;
}

// Pure: per platform, per campaign, with our sign-ups and paid orders by the chosen touch.
export function summarizeAds({ campaigns, daily, ads, adDaily, signups, orders, pulls, connections }, touchKind = 'last_touch') {
  const out = {};
  PLATFORMS.forEach((p) => { out[p.key] = { ...p, connected: false, connectedAt: null, lastPull: null, rows: {}, ads: {}, spend: 0, currency: null, unassigned: { signups: 0, orders: 0 } }; });
  // Signed in by OAuth (Snapchat): connected even before the first campaign arrives.
  (connections || []).forEach((c) => { const o = out[c.platform]; if (o) { o.connected = true; o.connectedAt = c.connected_at; } });
  (pulls || []).forEach((r) => { const o = out[r.platform]; if (o && (!o.lastPull || r.started_at > o.lastPull.started_at)) o.lastPull = r; });
  // A token-based platform (Meta) is connected once a pull has succeeded, even with no campaign yet.
  Object.values(out).forEach((o) => { if (o.lastPull?.status === 'ok') o.connected = true; });
  const row = (pl, id) => {
    const o = out[pl];
    if (!o.rows[id]) o.rows[id] = { campaign_id: id, name: null, status: null, spend: 0, impressions: 0, clicks: 0, installs: 0, conversions: 0, signups: 0, orders: 0 };
    return o.rows[id];
  };
  const adRow = (pl, id) => {
    const o = out[pl];
    if (!o.ads[id]) o.ads[id] = { ad_id: id, campaign_id: null, name: null, status: null, spend: 0, impressions: 0, clicks: 0, installs: 0, conversions: 0, signups: 0, orders: 0 };
    return o.ads[id];
  };
  (ads || []).forEach((a) => { if (out[a.platform]) Object.assign(adRow(a.platform, a.ad_id), { campaign_id: a.campaign_id, name: a.name, status: a.status }); });
  (adDaily || []).forEach((d) => {
    if (!out[d.platform]) return;
    const r = adRow(d.platform, d.ad_id);
    r.campaign_id = r.campaign_id || d.campaign_id;
    ['spend', 'impressions', 'clicks', 'installs', 'conversions'].forEach((k) => { r[k] += Number(d[k]) || 0; });
  });
  (campaigns || []).forEach((c) => { if (!out[c.platform]) return; out[c.platform].connected = true; Object.assign(row(c.platform, c.campaign_id), { name: c.name, status: c.status }); });
  (daily || []).forEach((d) => {
    if (!out[d.platform]) return;
    const r = row(d.platform, d.campaign_id);
    ['spend', 'impressions', 'clicks', 'installs', 'conversions'].forEach((k) => { r[k] += Number(d[k]) || 0; });
    out[d.platform].spend += Number(d.spend) || 0;
    out[d.platform].currency = out[d.platform].currency || d.currency;
    out[d.platform].connected = true;
  });
  const credit = (list, field) => (list || []).forEach((a) => {
    const c = creditOf(a[touchKind] || {});
    if (!c || !out[c.platform]) return;
    if (c.campaign_id && out[c.platform].rows[c.campaign_id]) out[c.platform].rows[c.campaign_id][field] += 1;
    else out[c.platform].unassigned[field] += 1;
    // the ad as well, when the link named one we know (Meta: utm_content = {{ad.id}})
    if (c.ad_id && out[c.platform].ads[c.ad_id]) out[c.platform].ads[c.ad_id][field] += 1;
  });
  credit(signups, 'signups');
  credit(orders, 'orders');
  Object.values(out).forEach((o) => {
    o.spend = Math.round(o.spend * 100) / 100;
    o.list = Object.values(o.rows).sort((a, b) => b.spend - a.spend || String(a.name).localeCompare(String(b.name)));
    o.adList = Object.values(o.ads).sort((a, b) => b.spend - a.spend || String(a.name).localeCompare(String(b.name)));
  });
  return out;
}

export async function fetchAds(range, win) {
  const [camps, daily, ads, adDaily, pulls, signups, paid, conns] = await Promise.all([
    sb.from('growth_ad_campaigns').select('platform, campaign_id, name, status, channel'),
    sb.from('growth_ad_daily').select('platform, campaign_id, day, currency, spend, impressions, clicks, installs, conversions')
      .gte('day', range.from).lte('day', range.to).limit(20000),
    sb.from('growth_ad_ads').select('platform, ad_id, campaign_id, name, status'),
    sb.from('growth_ad_ad_daily').select('platform, ad_id, campaign_id, day, spend, impressions, clicks, installs, conversions')
      .gte('day', range.from).lte('day', range.to).limit(20000),
    sb.from('growth_ad_pulls').select('platform, status, started_at, finished_at, error').order('started_at', { ascending: false }).limit(20),
    sb.from('growth_attribution').select('first_touch, last_touch').eq('kind', 'signup')
      .gte('recorded_at', new Date(win.start).toISOString()).lt('recorded_at', new Date(win.end).toISOString()),
    sb.from('market_orders').select('id').not('paid_at', 'is', null)
      .gte('paid_at', new Date(win.start).toISOString()).lt('paid_at', new Date(win.end).toISOString()),
    sb.from('growth_ad_connections').select('platform, connected_at'),
  ]);
  for (const r of [camps, daily, ads, adDaily, pulls, signups, paid, conns]) if (r.error) throw r.error;
  const ids = (paid.data || []).map((o) => o.id);
  let orders = [];
  if (ids.length) {
    const { data, error } = await sb.from('growth_attribution').select('first_touch, last_touch').eq('kind', 'order').in('subject_id', ids);
    if (error) throw error;
    orders = data || [];
  }
  return { campaigns: camps.data || [], daily: daily.data || [], ads: ads.data || [], adDaily: adDaily.data || [], pulls: pulls.data || [], signups: signups.data || [], orders, connections: conns.data || [] };
}

// The one-time sign-in link (owner only, single-use, 30 minutes). The browser
// goes to ads-oauth-callback, which forwards it to the platform's sign-in;
// the token never reaches the browser.
export const OAUTH_START = 'https://utzalmszfqfcofywfetv.supabase.co/functions/v1/ads-oauth-callback?start=';
export async function beginAdsOAuth(platform) {
  const { data, error } = await sb.rpc('growth_ads_oauth_begin', { p_platform: platform });
  if (error) throw error;
  return OAUTH_START + data;
}

// What ads-oauth-callback reports back in ?adlink=.
export const ADLINK_AR = {
  connected: { ok: true, text: 'تم ربط Snapchat للقراءة فقط. السحب الأول يعمل الآن؛ النتيجة في «آخر سحب».' },
  denied: { ok: false, text: 'أُلغي الربط من صفحة Snapchat. لم يُحفظ شيء.' },
  expired: { ok: false, text: 'الرابط منتهٍ أو استُخدم من قبل. اطلب رابطاً جديداً.' },
  error: { ok: false, text: 'تعذّر الربط. التفاصيل في سجل الدالة.' },
};

// «حالة التكاملات» for an ad platform, from its last pull.
export function adStatus(pulls, platform) {
  const last = (pulls || []).filter((p) => p.platform === platform)[0];
  if (!last) return null;
  if (last.status === 'error') return { state: 'bad', label: 'فشل آخر سحب' };
  const fresh = Date.now() - Date.parse(last.finished_at || last.started_at) < 36 * 3600e3;
  return fresh ? { state: 'ok', label: 'متصل · قراءة فقط' } : { state: 'bad', label: 'السحب متأخر' };
}
